import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useJobProgress, JobProgress } from './JobProgressContext'; // Import useJobProgress

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const { updateJobProgress } = useJobProgress(); // Get the update function
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (token) {
      // Use runtime config first, then fall back to env vars
      const runtimeConfig = (window as any).__CLIPOID_CONFIG__;
      const socketUrl = runtimeConfig?.SOCKET_URL || 
                       import.meta.env['VITE_SOCKET_URL'] || 
                       (import.meta.env.DEV ? 'http://localhost:4001' : '');
      
      console.log(`[Socket.IO] Runtime config:`, runtimeConfig);
      console.log(`[Socket.IO] Connecting to: ${socketUrl || 'same-origin'} - Runtime URL: ${runtimeConfig?.SOCKET_URL} - ENV URL: ${import.meta.env['VITE_SOCKET_URL']}`);

      // Check if we're in hotfix mode (production URL but with development-like behavior)
      const isHotfixMode = socketUrl.includes('api.clipoid.com');
      const isDevelopment = import.meta.env.DEV || isHotfixMode;
      
      const newSocket = io(socketUrl, {
        // The 'auth' payload is removed. Authentication will now rely solely on the
        // httpOnly cookie ('auth_token') sent with the request, which is enabled by `withCredentials`.
        // This aligns WebSocket authentication with the standard HTTP request authentication.
        transports: ['polling', 'websocket'], // Allow both polling and websocket
        withCredentials: true, // Ensures cookies (like auth_token) are sent with the connection request
        
        // Enhanced reconnection for development/hotfix to handle restarts
        reconnection: true,
        reconnectionAttempts: isDevelopment ? 20 : 5, // More attempts in dev/hotfix
        reconnectionDelay: isDevelopment ? 500 : 1000, // Faster retry in dev/hotfix
        reconnectionDelayMax: isDevelopment ? 5000 : 5000, // Same max delay
        // maxReconnectionAttempts is not a valid Socket.IO option, using reconnectionAttempts instead
        timeout: isDevelopment ? 30000 : 20000, // Longer timeout in development
      });

      setSocket(newSocket);

      newSocket.on('connect', () => {
        console.log('[Socket.IO] Connected to server via context.');
        // Don't set connected until authenticated
        // Send authentication immediately after connection
        if (token) {
          console.log('[Socket.IO] Sending authentication token after connection');
          newSocket.emit('authenticate', { token });
        }
      });

      newSocket.on('disconnect', () => {
        console.log('[Socket.IO] Disconnected from server via context.');
        setIsConnected(false);
      });

      newSocket.on('connect_error', (error: Error) => {
        console.error('[Socket.IO] Connection error via context:', error);
      });

      newSocket.on('reconnect', (attemptNumber: number) => {
        console.log(`[Socket.IO] Reconnected after ${attemptNumber} attempts`);
        setIsConnected(true);
        // Re-authenticate after reconnection
        if (token) {
          console.log('[Socket.IO] Re-authenticating after reconnection');
          newSocket.emit('authenticate', { token });
        }
        // Request current progress after reconnection
        setTimeout(() => {
          console.log('[Socket.IO] Requesting current job progress after reconnection');
          newSocket.emit('request_all_job_progress');
        }, 100);
      });

      newSocket.on('reconnect_attempt', (attemptNumber: number) => {
        if (isDevelopment) {
          console.log(`[Socket.IO] Reconnection attempt ${attemptNumber} (development mode)`);
        }
      });

      newSocket.on('reconnect_failed', () => {
        console.warn('[Socket.IO] Failed to reconnect after all attempts');
        setIsConnected(false);
      });

      // Handle authentication events
      newSocket.on('authentication_required', (data: { message: string }) => {
        console.log('[Socket.IO] Authentication required:', data.message);
        // Send authentication with the token
        if (token) {
          console.log('[Socket.IO] Sending authentication token');
          newSocket.emit('authenticate', { token });
        }
      });

      newSocket.on('authenticated', (data: { userId: number }) => {
        console.log('[Socket.IO] Successfully authenticated, userId:', data.userId);
        setIsConnected(true);
        
        // Request current progress for all active jobs after authentication
        console.log('[Socket.IO] Requesting current job progress after authentication');
        newSocket.emit('request_all_job_progress');
      });

      newSocket.on('authentication_failed', (data: { error: string }) => {
        console.error('[Socket.IO] Authentication failed:', data.error);
        setIsConnected(false);
      });

      newSocket.on('already_authenticated', (data: { userId: number }) => {
        console.log('[Socket.IO] Already authenticated, userId:', data.userId);
        setIsConnected(true);
      });

      // Debug all Socket.IO events
      newSocket.onAny((event, ...args) => {
        console.log(`[Socket.IO] Received event: ${event}`, args);
      });

      // Listen for job updates from the backend
      newSocket.on('job_update', (data: JobProgress) => {
        // Enhanced logging for debugging progress issues
        let progressInfo = '';
        if (data.downloadProgress !== undefined) progressInfo += `download:${data.downloadProgress}% `;
        if (data.subtitleProgress !== undefined) progressInfo += `subtitle:${data.subtitleProgress}% `;
        if (data.progress !== undefined) progressInfo += `generic:${data.progress}% `;
        if (data.clipsProcessedCount !== undefined && data.clipsTotalCount !== undefined) {
          progressInfo += `clips:${data.clipsProcessedCount}/${data.clipsTotalCount} `;
        }
        
        console.log(`🔥 [Socket.IO REAL-TIME] Job ${data.jobId} | Stage: ${data.status} | ${progressInfo}`, {
          displayText: data.displayText,
          downloadProgress: data.downloadProgress,
          subtitleProgress: data.subtitleProgress,
          status: data.status,
          timestamp: new Date().toISOString()
        });
        
        if (data && data.jobId) {
          // 🚀 CRITICAL: Mark as Socket.IO source for priority handling
          updateJobProgress(data.jobId, data, 'socket');
        } else {
          console.warn('[SocketContext] Received invalid job_update data:', data);
        }
      });

      // Listen for job failure events for immediate UI update
      newSocket.on('job_failed', (data: { jobId: string; error: string; status: string }) => {
        console.error(`❌ [Socket.IO] Job ${data.jobId} failed:`, data.error);
        
        if (data && data.jobId) {
          // Immediately update job as failed
          updateJobProgress(data.jobId, {
            jobId: data.jobId,
            status: 'FAILED',
            displayText: `Failed: ${data.error}`,
            timestamp: Date.now()
          }, 'socket');
        }
      });

      // Listen for thumbnail refresh events
      newSocket.on('jobThumbnailRefreshed', (data: { jobId: string; vodThumbnailUrl: string }) => {
        console.log(`[SocketContext] Thumbnail refreshed for job ${data.jobId}:`, data.vodThumbnailUrl);
        
        // Create a custom event to notify components about thumbnail refresh
        const event = new CustomEvent('jobThumbnailRefreshed', { 
          detail: data 
        });
        window.dispatchEvent(event);
      });

      // 🚀 Listen for immediate clip creation events
      newSocket.on('clip_created', (data: { 
        jobId: string; 
        clip: any; 
        clipIndex: number; 
        totalClips: number; 
        timestamp: number;
      }) => {
        console.log(`[SocketContext] 🚀 Clip created for job ${data.jobId}: clip ${data.clipIndex}/${data.totalClips} - ${data.clip.title}`);
        
        // Create a custom event to notify components about new clip
        const event = new CustomEvent('clipCreated', { 
          detail: data 
        });
        window.dispatchEvent(event);
      });

      return () => {
        console.log('[Socket.IO] Disconnecting socket via context cleanup.');
        newSocket.disconnect();
      };
    } else {
      if (socket) {
        console.log('[Socket.IO] No token, disconnecting existing socket.');
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
    }
    // Return a cleanup function in all cases to satisfy TypeScript
    return () => {
      if (socket && !token) {
          socket.disconnect();
      }
    };
  }, [token]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};